import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Save, Mail, Server, Send, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailSettings = () => {
  const [settings, setSettings] = useState({
    smtp_host: 'smtpout.secureserver.net',
    smtp_port: 465,
    smtp_username: 'heeloly@heelolyverse.com',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_start_tls: false,
    smtp_enabled: false,
    from_email: 'heeloly@heelolyverse.com',
    from_name: 'Heeloly Upasani',
    site_url: 'https://heelolyverse.com'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-settings`);
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Failed to fetch email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-settings`, settings);
      toast.success('Email settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    setTesting(true);
    try {
      await axios.post(`${API}/admin/test-email`, { test_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  return (
    <div className="admin-section">
      <h2 className="section-subtitle" style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Server size={24} />
        SMTP Email Settings
      </h2>

      <div className="admin-form">
        <form onSubmit={handleSave}>
          {/* SMTP Enable Toggle */}
          <div className="form-group" style={{ marginBottom: '30px', padding: '20px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <Label style={{ fontSize: '1.1rem' }}>Enable SMTP</Label>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '5px' }}>
                  Enable email sending functionality
                </p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.smtp_enabled}
                  onChange={(e) => setSettings({ ...settings, smtp_enabled: e.target.checked })}
                  style={{ width: '20px', height: '20px', marginRight: '10px' }}
                />
                {settings.smtp_enabled ? (
                  <CheckCircle size={24} style={{ color: 'var(--accent-gold)' }} />
                ) : (
                  <XCircle size={24} style={{ color: 'var(--text-muted)' }} />
                )}
              </label>
            </div>
          </div>

          {/* SMTP Server Settings */}
          <div style={{ marginBottom: '30px', paddingBottom: '30px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 style={{ color: 'var(--accent-gold)', marginBottom: '20px', fontSize: '1.1rem' }}>Server Configuration</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <Label>SMTP Host</Label>
                <Input
                  value={settings.smtp_host}
                  onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                  placeholder="smtpout.secureserver.net"
                />
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '5px' }}>
                  GoDaddy: smtpout.secureserver.net
                </p>
              </div>
              <div className="form-group">
                <Label>SMTP Port</Label>
                <Input
                  type="number"
                  value={settings.smtp_port}
                  onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                  placeholder="465"
                />
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '5px' }}>
                  SSL: 465, TLS: 587
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
              <div className="form-group">
                <Label>SMTP Username</Label>
                <Input
                  value={settings.smtp_username}
                  onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                  placeholder="heeloly@heelolyverse.com"
                />
              </div>
              <div className="form-group">
                <Label>SMTP Password</Label>
                <Input
                  type="password"
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder="Enter password"
                />
              </div>
            </div>

            <div style={{ marginTop: '15px', padding: '15px', background: 'rgba(212, 175, 55, 0.1)', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
              <p style={{ color: 'var(--accent-gold)', fontSize: '0.9rem', margin: 0 }}>
                <strong>GoDaddy Settings:</strong> Use port 465 with SSL. The correct TLS/SSL settings will be applied automatically based on the port.
              </p>
            </div>
          </div>

          {/* Sender Settings */}
          <div style={{ marginBottom: '30px', paddingBottom: '30px', borderBottom: '1px solid var(--border-color)' }}>
            <h3 style={{ color: 'var(--accent-gold)', marginBottom: '20px', fontSize: '1.1rem' }}>Sender Information</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <Label>From Email</Label>
                <Input
                  type="email"
                  value={settings.from_email}
                  onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                  placeholder="heeloly@heelolyverse.com"
                />
              </div>
              <div className="form-group">
                <Label>From Name</Label>
                <Input
                  value={settings.from_name}
                  onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                  placeholder="Heeloly Upasani"
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '20px' }}>
              <Label>Website URL (for unsubscribe links)</Label>
              <Input
                value={settings.site_url}
                onChange={(e) => setSettings({ ...settings, site_url: e.target.value })}
                placeholder="https://heelolyverse.com"
              />
            </div>
          </div>

          {/* Test Email */}
          <div style={{ marginBottom: '30px', padding: '20px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
            <h3 style={{ color: 'var(--accent-silver)', marginBottom: '15px', fontSize: '1rem' }}>Test Configuration</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email to send test"
                style={{ flex: 1 }}
              />
              <Button type="button" onClick={handleTestEmail} disabled={testing} className="btn-secondary">
                <Send size={16} style={{ marginRight: '8px' }} />
                {testing ? 'Sending...' : 'Send Test'}
              </Button>
            </div>
          </div>

          <Button type="submit" className="btn-primary" disabled={saving}>
            <Save size={18} style={{ marginRight: '8px' }} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default EmailSettings;
